import React, { useRef, useState } from "react"
import { IconButton, ListItemIcon, Menu, MenuItem, Theme, Typography, makeStyles } from "@material-ui/core"
import { CornerUpLeft, Edit2, MoreVertical, Smile, Trash2 } from "react-feather"
import { useTranslation } from "react-i18next"

type Props = {
  canModify?: boolean
  pinned?: boolean
  onReply: () => void
  onAddReaction: (anchorEl: HTMLElement) => void
  onTogglePin: () => void
  onEdit: () => void
  onDelete: () => void
}

const PinIcon = ({ size = 16 }: { size?: number }) => (
  <span style={{ fontSize: size, lineHeight: 1 }} aria-hidden>
    📌
  </span>
)

const MessageActionsMenu = ({ canModify, pinned, onReply, onAddReaction, onTogglePin, onEdit, onDelete }: Props) => {
  const classes = useStyles()
  const { t } = useTranslation("chat")
  const { t: common } = useTranslation("translation")

  const triggerRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)

  // Closing first means the emoji picker anchors to the trigger, not to a menu item
  // that is about to disappear.
  const runAndClose = (action: () => void) => () => {
    setOpen(false)
    action()
  }

  return (
    <>
      <IconButton
        ref={triggerRef}
        size="small"
        title={t("messageActionsMenu.triggerHint")}
        aria-label={t("messageActionsMenu.triggerHint")}
        onClick={() => setOpen(true)}
      >
        <MoreVertical size={16} />
      </IconButton>

      <Menu
        open={open}
        anchorEl={triggerRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        getContentAnchorEl={null}
      >
        <MenuItem onClick={runAndClose(onReply)}>
          <ListItemIcon className={classes.icon}>
            <CornerUpLeft size={16} />
          </ListItemIcon>

          <Typography variant="body2">{t("messageActionsMenu.replyLabel")}</Typography>
        </MenuItem>

        <MenuItem onClick={runAndClose(() => triggerRef.current && onAddReaction(triggerRef.current))}>
          <ListItemIcon className={classes.icon}>
            <Smile size={16} />
          </ListItemIcon>

          <Typography variant="body2">{t("messageActionsMenu.addReactionLabel")}</Typography>
        </MenuItem>

        <MenuItem onClick={runAndClose(onTogglePin)}>
          <ListItemIcon className={classes.icon}>
            <PinIcon />
          </ListItemIcon>

          <Typography variant="body2">{pinned ? t("unpinLabel") : t("pinLabel")}</Typography>
        </MenuItem>

        {canModify && (
          <MenuItem onClick={runAndClose(onEdit)}>
            <ListItemIcon className={classes.icon}>
              <Edit2 size={16} />
            </ListItemIcon>

            <Typography variant="body2">{t("messageActionsMenu.editLabel")}</Typography>
          </MenuItem>
        )}

        {canModify && (
          <MenuItem onClick={runAndClose(onDelete)}>
            <ListItemIcon className={classes.icon}>
              <Trash2 size={16} />
            </ListItemIcon>

            <Typography variant="body2">{common("deleteLabel")}</Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  icon: {
    minWidth: theme.spacing(4),
  },
}))

export default MessageActionsMenu
